/**
 * SignalR client for the Football "goalshooting" multiplayer hub.
 * Port of
 *   packages/amgfootball_v2/lib/data/repositories/football_multiplayer_service.dart
 *   + packages/amgcommon/lib/socketConnection.dart (SocketGameHub wrapper)
 *
 * Wire format (from Flutter source):
 *   connect()       — HubConnectionBuilder().withAutomaticReconnect().build()
 *   joinGame(sid,g) — invoke('JoinGame', [sessionId, gameKey])    (sessionId = URL-decoded token)
 *   getMessages()   — invoke('GetMessages', [])
 *   placeBet(id,a)  — invoke('PlaceBet', [betItemId, amount])     (both ints)
 *
 * Server pushes 8 events: PlayerJoined / StartGame / StartBet / EndBet /
 * StartPlay / Result / PlaceBet / GetMessages.
 * Each event's `args` is normalised: single-element arrays unwrapped, JSON
 * strings auto-decoded — same as `_normalizePayload` in the Flutter service.
 *
 * Connection lifecycle is exposed via a synthetic `_connection` event with
 * payload `{ state: 'connected' | 'reconnecting' | 'reconnected' | 'closed',
 * connectionId?, error? }`.
 */
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { debugLog, warn } from '../util/logger';

export interface HubEvent {
  /** Server event name, or `_connection` for connection-state changes. */
  name: string;
  payload: unknown;
}

export type HubEventListener = (event: HubEvent) => void;

/**
 * The 8 server events the Football hub emits. Registered on connect — matches
 * `mp.registerHandlers([...])` in game_bloc.dart:814-823. (Note: no PlayerLeft.)
 */
export const HUB_EVENTS = [
  'PlayerJoined',
  'StartGame',
  'StartBet',
  'EndBet',
  'StartPlay',
  'Result',
  'PlaceBet',
  'GetMessages',
] as const;
export type HubEventName = (typeof HUB_EVENTS)[number] | '_connection';

export class MultiplayerHub {
  private readonly hubUrl: string;
  private connection: HubConnection | null = null;
  private connected = false;
  private listeners: Set<HubEventListener> = new Set();

  // Cached so we can re-JoinGame on reconnect (mirror _lastSessionId/_lastGameKey).
  private lastSessionId: string | null = null;
  private lastGameKey: string | null = null;

  constructor(hubUrl: string) {
    this.hubUrl = hubUrl;
  }

  isConnected(): boolean {
    return this.connection != null && this.connected;
  }

  on(listener: HubEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async connect(): Promise<void> {
    if (this.connection) return;

    debugLog('[Hub] connecting to', this.hubUrl);
    this.connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        // The server returns `Access-Control-Allow-Origin: *`, which is
        // incompatible with the SignalR JS client's default credentialed
        // requests. Auth is passed inside JoinGame args anyway, so we don't
        // need cookie/credential forwarding. (The Dart client doesn't set
        // credentials either, but the browser client defaults to true.)
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.onclose((error) => {
      this.connected = false;
      debugLog('[Hub] closed:', error);
      this.emit('_connection', { state: 'closed', error: errMsg(error) });
    });
    this.connection.onreconnecting((error) => {
      this.connected = false;
      debugLog('[Hub] reconnecting:', error);
      this.emit('_connection', { state: 'reconnecting', error: errMsg(error) });
    });
    this.connection.onreconnected((connectionId) => {
      this.connected = true;
      debugLog('[Hub] reconnected', connectionId);
      this.emit('_connection', {
        state: 'reconnected',
        connectionId: connectionId ?? null,
      });
      void this.resumeAfterReconnect();
    });

    for (const name of HUB_EVENTS) {
      this.connection.on(name, (...args: unknown[]) => {
        const payload = normalizePayload(args);
        debugLog('[Hub] event', name, payload);
        this.emit(name, payload);
      });
    }

    try {
      await this.connection.start();
      this.connected = true;
      debugLog('[Hub] connected');
      this.emit('_connection', { state: 'connected' });
    } catch (e) {
      this.connected = false;
      warn('[Hub] start failed:', e);
      throw e;
    }
  }

  async disconnect(): Promise<void> {
    const c = this.connection;
    this.connection = null;
    this.connected = false;
    if (!c) return;
    try {
      await c.stop();
    } catch (e) {
      warn('[Hub] stop failed (ignored):', e);
    }
  }

  // -----------------------------------------------------------------
  // Client → server methods
  // -----------------------------------------------------------------

  async joinGame(sessionId: string, gameKey: string): Promise<void> {
    this.lastSessionId = sessionId;
    this.lastGameKey = gameKey;
    await this.invoke('JoinGame', sessionId, gameKey);
  }

  async getMessages(): Promise<void> {
    await this.invoke('GetMessages');
  }

  /** Send a bet to the hub. Server then echoes a PlaceBet event. Both args ints. */
  async placeBet(betItemId: number, amount: number): Promise<void> {
    await this.invoke('PlaceBet', Math.round(betItemId), Math.round(amount));
  }

  private async invoke(name: string, ...args: unknown[]): Promise<void> {
    const c = this.connection;
    if (!c || c.state !== HubConnectionState.Connected) {
      warn(`[Hub] cannot invoke '${name}' — not connected (state=${c?.state})`);
      return;
    }
    debugLog('[Hub] invoke', name, args);
    try {
      await c.invoke(name, ...args);
    } catch (e) {
      warn(`[Hub] invoke '${name}' failed:`, e);
    }
  }

  // -----------------------------------------------------------------
  // Reconnect resume — re-JoinGame then GetMessages, same order as connect.
  // -----------------------------------------------------------------

  private async resumeAfterReconnect(): Promise<void> {
    if (!this.lastSessionId || !this.lastGameKey) return;
    try {
      await this.joinGame(this.lastSessionId, this.lastGameKey);
      await this.getMessages();
    } catch (e) {
      warn('[Hub] resume after reconnect failed:', e);
    }
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  private emit(name: string, payload: unknown): void {
    const event: HubEvent = { name, payload };
    for (const l of this.listeners) {
      try {
        l(event);
      } catch (e) {
        warn('[Hub] listener threw:', e);
      }
    }
  }
}

/**
 * Mirror of Flutter `_normalizePayload(List<dynamic>? args)`:
 *   - empty → null
 *   - single element → unwrap; if it's a JSON string, parse it
 *   - many elements → map(parse JSON string if applicable)
 */
function normalizePayload(args: unknown[]): unknown {
  if (!args || args.length === 0) return null;
  if (args.length === 1) {
    return parseIfJsonString(args[0]);
  }
  return args.map(parseIfJsonString);
}

function parseIfJsonString(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const s = v.trim();
  if (s.length === 0) return v;
  const ch = s[0];
  if (ch !== '{' && ch !== '[' && ch !== '"') return v;
  try {
    return JSON.parse(s);
  } catch {
    return v;
  }
}

function errMsg(e: unknown): string | null {
  if (e == null) return null;
  if (e instanceof Error) return e.message;
  return String(e);
}
